import { FC, HTMLAttributes, ReactNode } from "react";
import Image from "next/image";
import Link from "next/link";
import clsx from "clsx";
import { format } from "date-fns";

// ------------------------------------------------------------------------------------------
// Modernized blog card.
//
//   - Whole card is one Link so the entire surface is clickable (used to
//     be three separate Link nodes wrapping image, title, and button).
//   - Aspect-ratio image with hover zoom; figureWrapperClass is honored
//     so existing featured / grid call-sites can still override.
//   - Real category chip (`technical` / `leadership`) replaces the old
//     hard-coded "Category" label that always rendered the literal word.
//   - Description renders raw children — no HtmlText prose stack — and
//     line-clamps to 2 lines so cards stay uniform height.
//   - Hover state: -translate + shadow + border tint + image scale +
//     title color shift. All on a single group root so they fire together.
// ------------------------------------------------------------------------------------------

export type IBlogCard = {
  publishDate?: string;
  readingTime?: string;
  heading?: string;
  description?: ReactNode;
  link: string;
  showReadArticleButton?: boolean;
  image?: { src: string; alt: string };
  author?: {
    name?: string;
    image?: string;
  };
  /**
   * Used to render a category chip on top of the hero image. Pass the
   * blog's `category` field. The legacy `categoryBadge` boolean is
   * preserved as a fallback enable flag.
   */
  category?: "technical" | "leadership" | string;
  /** Legacy enable flag — keep card chip visible even when category absent */
  categoryBadge?: boolean;
  /** Cosmetic background — kept for backward compat but de-emphasised */
  variant?: "white" | "primary";
};

export type IBlogCardProps = HTMLAttributes<HTMLDivElement> &
  IBlogCard & {
    imageClass?: string;
    figureClass?: string;
    figureWrapperClass?: string;
    contentWrapperClass?: string;
  };

const variantBg = {
  white: "bg-white dark:bg-black-900",
  primary: "bg-primary-50 dark:bg-black-900",
};

const categoryLabels: Record<string, { label: string; chip: string }> = {
  technical: {
    label: "Technical",
    chip: "bg-primary-900/95 text-white",
  },
  leadership: {
    label: "Leadership",
    chip: "bg-yellow-400 text-black-900",
  },
};

const BlogCard: FC<IBlogCardProps> = (props) => {
  const {
    heading,
    description,
    showReadArticleButton,
    link,
    image,
    publishDate,
    readingTime,
    author,
    category,
    categoryBadge,
    variant = "white",
    className,
    imageClass,
    figureClass,
    figureWrapperClass,
    contentWrapperClass,
    ...other
  } = props;

  const date = publishDate && format(publishDate, "MMM d, yyyy");
  const categoryMeta =
    (category && categoryLabels[category]) ||
    (categoryBadge ? { label: "Article", chip: "bg-black-900/85 text-white" } : undefined);

  return (
    <Link
      href={link}
      className={clsx(
        // Card root — compact, standard-sized. Single source of hover state
        // for all children via `group`.
        "group relative flex flex-col overflow-hidden rounded-xl border border-black-100 transition-all duration-300",
        "hover:-translate-y-0.5 hover:border-primary-200 hover:shadow-[0_8px_24px_-12px_rgba(31,42,73,0.18)]",
        "dark:border-black-800 dark:hover:border-primary-700",
        variantBg[variant],
        className,
      )}
      {...other as HTMLAttributes<HTMLAnchorElement>}
    >
      {/* ---------- Hero image with zoom on hover ----------
          16:9 default matches how blog hero images are typically designed,
          so the image renders at its natural framing (no apparent crop /
          zoom). The zoom kicks in only on hover. */}
      <div className={clsx(figureClass)}>
        <figure
          className={clsx(
            "relative overflow-hidden bg-black-50 dark:bg-black-800",
            figureWrapperClass ? figureWrapperClass : "aspect-video w-full",
          )}
        >
          {categoryMeta && (
            <span
              className={clsx(
                "absolute left-3 top-3 z-10 rounded-full px-2.5 py-0.5 text-[11px] font-medium tracking-wide backdrop-blur-sm",
                categoryMeta.chip,
              )}
            >
              {categoryMeta.label}
            </span>
          )}

          {image?.src ? (
            <Image
              src={image.src}
              alt={image.alt}
              fill
              sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw"
              className={clsx(
                "object-cover transition-transform duration-500 group-hover:scale-[1.04]",
                imageClass,
              )}
              unoptimized
            />
          ) : (
            // Placeholder for blogs without a hero — keeps card height stable
            <div className="flex h-full w-full items-center justify-center text-sm font-normal text-black-400">
              No image
            </div>
          )}
        </figure>
      </div>

      {/* ---------- Content ---------- */}
      <div
        className={clsx(
          "flex flex-1 flex-col gap-2 p-4 sm:p-4 lg:p-5",
          contentWrapperClass,
        )}
      >
        {/* Meta — monospace-ish styling matches editorial / "tech blog" feel
            (date · read time, both uppercase and tracked). */}
        {(date || readingTime) && (
          <div className="flex items-center gap-2 font-mono text-[10px] font-normal uppercase tracking-[0.08em] text-black-500 dark:text-black-400">
            {date && <span>{date}</span>}
            {date && readingTime && (
              <span aria-hidden="true" className="text-black-400">•</span>
            )}
            {readingTime && <span>{readingTime} min</span>}
          </div>
        )}

        {/* Title — hero of the card. Bigger clamp, more line height, two-line clamp. */}
        {heading && (
          <h3
            className={clsx(
              "line-clamp-3 font-bricolage-grotesque text-[clamp(1.125rem,0.5vw+0.9375rem,1.5rem)] font-medium !leading-[1.2] text-black-900 transition-colors duration-300 group-hover:text-primary-900 dark:text-white",
            )}
          >
            {heading}
          </h3>
        )}

        {/* Description — kept minimal so title dominates. 1 line only. */}
        {description && (
          <div
            className={clsx(
              "line-clamp-1 text-[12px] leading-relaxed text-black-600 dark:text-black-400",
              "[&_p]:m-0 [&_*]:!text-inherit [&_a]:pointer-events-none",
            )}
          >
            {description}
          </div>
        )}

        {/* Spacer pushes footer to bottom so uneven content keeps cards aligned */}
        <div className="grow" />

        {/* Footer row — author byline on the left, optional Read more CTA
            on the right. Single row keeps them visually related instead
            of having the CTA float orphaned below the byline (which was
            especially ugly on the tall featured card layout). */}
        {(author?.name || showReadArticleButton) && (
          <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
            {author?.name ? (
              <div className="flex items-center gap-2">
                {author.image && (
                  <Image
                    className="h-5 w-5 rounded-full object-cover ring-1 ring-black-100 dark:ring-black-800"
                    src={author.image}
                    alt={author.name}
                    height={20}
                    width={20}
                    unoptimized
                  />
                )}
                <span className="font-mono text-[10px] font-normal uppercase tracking-[0.08em] text-black-600 dark:text-black-400">
                  By {author.name}
                </span>
              </div>
            ) : (
              // Placeholder div so justify-between still aligns CTA right
              // when there's no author on the card.
              <span />
            )}

            {showReadArticleButton && (
              // Rendered as a <span>, not <a>/<button>, because the whole
              // card is already wrapped in a <Link>. A nested anchor here
              // would invalidate the HTML and trigger hydration mismatch.
              <span
                id={`read-article-${link.split("/").filter(Boolean).pop() ?? ""}`}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-900 transition-colors duration-200 group-hover:text-primary-800 dark:text-primary-300"
              >
                Read more
                <span
                  aria-hidden="true"
                  className="transition-transform duration-200 group-hover:translate-x-0.5"
                >
                  →
                </span>
              </span>
            )}
          </div>
        )}
      </div>
    </Link>
  );
};

export default BlogCard;
